import React from 'react';
import { Alert, Box, Button, Grid, Paper, Typography } from '@mui/material';
import type { IncidentDrawerDetailScope } from './incidentDrawerDetailTypes';
import { incidentsApi } from '../../services/incidentsApi';
import {
  buildEvidenceDeliveryRows,
  clipFilenameLooksLikeReviewRing,
} from './incidentEvidenceTruth';

/**
 * Technical delivery and timing diagnostics, collapsed by default in grouped review.
 */
export function IncidentDrawerEvidenceTruthPanel({ scope }: { scope: IncidentDrawerDetailScope }) {
  const s = scope as Record<string, any>;
  const incident = s.selectedIncident;
  const [promoteBusy, setPromoteBusy] = React.useState<'snapshot' | 'clip' | 'both' | null>(null);
  const [promoteMessage, setPromoteMessage] = React.useState<string | null>(null);
  const [promoteError, setPromoteError] = React.useState<string | null>(null);

  if (!incident?.incident_id) return null;

  const incidentId = String(incident.incident_id);
  const canRequest = Boolean(incidentId) && !s.drawerBusy;

  const requestProof = async (mode: 'snapshot' | 'clip' | 'both') => {
    setPromoteBusy(mode);
    setPromoteMessage(null);
    setPromoteError(null);
    try {
      const res = await incidentsApi.promoteEvidence(incidentId, {
        snapshot: mode === 'snapshot' || mode === 'both',
        clip: mode === 'clip' || mode === 'both',
        promote_priority: 'operator',
      });
      const parts: string[] = [];
      if (res.promote_snapshot?.ok) parts.push('snapshot upload queued on edge');
      if (res.promote_clip?.ok) parts.push('clip upload queued on edge');
      if (parts.length) {
        setPromoteMessage(parts.join('; ') + '. Refresh evidence in a few seconds.');
      } else {
        setPromoteError('Edge did not accept the upload request (file may be missing on device).');
      }
      if (typeof s._refreshSelectedIncident === 'function') {
        await s._refreshSelectedIncident();
      }
    } catch (error: any) {
      const edgeDetail =
        error?.response?.data?.edge?.detail ||
        error?.response?.data?.edge?.response?.detail ||
        error?.response?.data?.edge?.response?.error;
      const promoteSnap = error?.response?.data?.promote_snapshot;
      const promoteClip = error?.response?.data?.promote_clip;
      setPromoteError(
        [
          error?.response?.data?.error,
          typeof promoteSnap === 'object' ? promoteSnap?.error : null,
          typeof promoteClip === 'object' ? promoteClip?.error : null,
          edgeDetail,
          error?.message,
        ]
          .filter(Boolean)
          .join(' — ') || 'Upload request failed',
      );
    } finally {
      setPromoteBusy(null);
    }
  };

  const hasCloudSnapshot =
    s.effectiveSnapshotStatus === 'AVAILABLE' || Boolean(s.latestSnapshotObjectUrl) || Boolean(s.hasSnapshotArtifact);
  const clipFilename =
    s.latestVideoAttachment?.filename ||
    s._bestVideoAttachment?.(incident)?.filename ||
    '';
  const hasCloudClip =
    s.effectiveClipStatus === 'AVAILABLE' || Boolean(s.latestVideoObjectUrl) || Boolean(s.hasClipArtifact);
  const clipOnEdge = Boolean((incident as any)?.clip_available_on_edge);
  const clipPromotionState = String((incident as any)?.clip_promotion_state || '');

  const deliveryRows = buildEvidenceDeliveryRows({
    incident,
    hasCloudSnapshot,
    hasCloudClip,
    clipFilename,
    clipOnEdge,
    clipPromotionState,
  });

  const reviewRing = hasCloudClip && clipFilenameLooksLikeReviewRing(clipFilename);
  const snapPendingClipOnly = hasCloudClip && !hasCloudSnapshot;
  const clipMisaligned = hasCloudClip && s.clipTiming?.covered === false;

  const format = (ms: number | null | undefined) =>
    ms && s.formatTimingInstant ? s.formatTimingInstant(ms) : '—';

  return (
    <Paper
      variant="outlined"
      sx={{ ...(s.drawerCardSx || {}), mb: 1.5, borderColor: '#c5d4e8', bgcolor: '#f8fafc' }}
      data-testid="evidence-truth-panel"
    >
      <Typography sx={{ ...(s.cardLabelSx || {}), mb: 0.5 }}>Delivery and timing diagnostics</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25, lineHeight: 1.55 }}>
        This panel shows what evidence reached the cloud and whether media timestamps line up. Do not confirm a risk
        pattern on badges alone.
      </Typography>

      {snapPendingClipOnly ? (
        <Alert severity="warning" sx={{ mb: 1.25 }}>
          Cloud has a clip but <strong>no snapshot</strong> for this incident. Treat the clip as unverified scene
          context until a snapshot arrives or edge timing confirms alignment.
        </Alert>
      ) : null}
      {reviewRing ? (
        <Alert severity="warning" sx={{ mb: 1.25 }}>
          Clip filename indicates a <strong>review-ring buffer segment</strong>, not necessarily the evidence-centered
          export. Check clip window against the evidence target below.
        </Alert>
      ) : null}
      {clipMisaligned ? (
        <Alert severity="error" sx={{ mb: 1.25 }}>
          Evidence target time falls <strong>outside</strong> this clip window — do not treat this clip as evidence proof.
        </Alert>
      ) : null}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 800, mb: 0.75 }}>
        Delivery ledger
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.25 }}>
        <Button
          size="small"
          variant="contained"
          disabled={!canRequest || promoteBusy !== null || hasCloudSnapshot}
          onClick={() => void requestProof('snapshot')}
        >
          {promoteBusy === 'snapshot' ? 'Requesting…' : 'Request snapshot upload'}
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={!canRequest || promoteBusy !== null || (hasCloudClip && !clipOnEdge)}
          onClick={() => void requestProof('clip')}
        >
          {promoteBusy === 'clip' ? 'Requesting…' : 'Request clip upload'}
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={
            !canRequest ||
            promoteBusy !== null ||
            (hasCloudSnapshot && hasCloudClip && !clipOnEdge)
          }
          onClick={() => void requestProof('both')}
        >
          {promoteBusy === 'both' ? 'Requesting…' : 'Request both'}
        </Button>
      </Box>
      {promoteMessage ? (
        <Alert severity="success" sx={{ mb: 1.25 }}>
          {promoteMessage}
        </Alert>
      ) : null}
      {promoteError ? (
        <Alert severity="warning" sx={{ mb: 1.25 }}>
          {promoteError}
        </Alert>
      ) : null}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Snapshots normally upload automatically on capture; clips stay on edge unless you request them (opening the
        review item also requests the clip once). Use the buttons when cloud proof is missing or upload is backlogged.
      </Typography>

      <Box component="ul" sx={{ m: 0, pl: 2.25, mb: 1.5 }}>
        {deliveryRows.map((row) => (
          <Box component="li" key={row.label} sx={{ mb: 0.65 }}>
            <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
              <strong>{row.label}:</strong>{' '}
              <Box
                component="span"
                sx={{
                  color:
                    row.tone === 'ok'
                      ? 'success.dark'
                      : row.tone === 'warn'
                        ? 'warning.dark'
                        : row.tone === 'error'
                          ? 'error.dark'
                          : 'text.secondary',
                }}
              >
                {row.value}
              </Box>
            </Typography>
          </Box>
        ))}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 800, mb: 0.75 }}>
        Timing truth
      </Typography>
      {s.evidenceTimingAvailable ? (
        <Grid container spacing={1.25}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary">
              Condition / observation start
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {format(s.eventStartedTimeMs)}
            </Typography>
          </Grid>
          {s.showSeparateAlertMoment ? (
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" color="text.secondary">
                Observation raised on edge
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {format(s.alertTriggeredTimeMs)}
              </Typography>
            </Grid>
          ) : null}
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary">
              Evidence target (frame anchor)
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {format(s.evidenceTargetTimeMs)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary">
              Proof capture requested
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {format(s.evidenceAnchorTimeMs)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary">
              Snapshot frame time
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {format(s.snapshotFrameTimeMs)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {s.snapshotDeltaMs != null
                ? `Δ from target: ${s.formatTimingDelta?.(s.snapshotDeltaMs) ?? `${s.snapshotDeltaMs}ms`}`
                : s.snapshotFrameTimeMs
                  ? 'Pixels are from this time'
                  : hasCloudSnapshot
                    ? 'No frame timestamp on attachment'
                    : 'No cloud snapshot yet'}
            </Typography>
          </Grid>
          {s.snapshotCapturedAtMs ? (
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" color="text.secondary">
                Snapshot evidence file written
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {format(s.snapshotCapturedAtMs)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                File production time on edge — not the camera frame time
              </Typography>
            </Grid>
          ) : null}
          {s.clipTiming?.capturedAtMs ? (
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" color="text.secondary">
                Clip evidence file written
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {format(s.clipTiming.capturedAtMs)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                File production time on edge — not the clip window
              </Typography>
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Clip window
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {s.clipTiming?.startMs && s.clipTiming?.endMs
                ? `${format(s.clipTiming.startMs)} → ${format(s.clipTiming.endMs)}`
                : hasCloudClip
                  ? 'Timing not embedded on clip metadata'
                  : '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {s.clipTiming?.covered === true
                ? 'Target falls inside this clip.'
                : s.clipTiming?.covered === false
                  ? `Target outside clip${s.clipTiming?.gapMs != null ? ` by ${s.formatTimingDelta?.(s.clipTiming.gapMs) ?? ''}` : ''}.`
                  : hasCloudClip
                    ? 'Alignment unknown — verify manually.'
                    : 'No cloud clip.'}
            </Typography>
          </Grid>
        </Grid>
      ) : (
        <Alert severity="info" sx={{ mt: 0.5 }}>
          Timing fields have not arrived from the edge for this review item yet. Detection may still be valid; proof
          timing is unknown.
        </Alert>
      )}

      {s.edgeDeliveryState?.title ? (
        <Box sx={{ mt: 1.35, pt: 1.1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
            Edge upload path
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.25 }}>
            {s.edgeDeliveryState.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {s.edgeDeliveryState.detail}
          </Typography>
        </Box>
      ) : null}
    </Paper>
  );
}
